import { useAuthActions, useAuthContext } from "@contexts/auth"
import { useEffect, useRef, useState } from "react"
import { useSearchParams } from "react-router-dom"
import {
  columns,
  dashboardPaths,
  graphColorLabels,
  graphColors,
  options,
  resolveDashboardPathPermission,
} from "./options"
import Tooltip from "@components/dashboard/tooltip"
import { useProjectActions, useProjectContext } from "@contexts/projects"
import { getFiles, uploadPresigned, uploadSmall } from "@utils/handleFiles"
import { useDashboardUIActions } from "@contexts/dashboardUI"
import ProcurementCyclesSection from "./ProcurementCyclesSection"
import PresentationModeBadge from "@components/dashboard/PresentationModeBadge"
import { isPresentationProject } from "@utils/presentationMode"
import TrendSettingsSection from "./TrendSettingsSection"

const DefaultHeader = ({ label, visible, handle }) => {
  return <div className="flex gap-8">
    <button
      className={`accordion-toggle ${visible ? "active" : ""}`}
      onClick={handle}
    ></button>
    <h2>{label}</h2>
  </div>
}

const CustomNameBuildingRow = ({ building }) => {
  const { updateBuildingInfo } = useProjectActions();
  const [custom, setCustom] = useState(building.custom_name || '')

  const handleSend = async () => {
    const res = await updateBuildingInfo(building.building_id, building.company_id, custom)
  }

  return <div>
    <p>Obra: {building.building_name}</p>
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
      <input style={{ width: '350px' }} className="default-input" value={custom} placeholder="Nome personalizado" onChange={(e) => setCustom(e.target.value)} />
      <button className="button button-secondary small" onClick={handleSend}>Enviar</button>
    </div>
  </div>
}

const findLabel = (i, arr) => {
  return arr?.find(card => card.value === i)?.label || i
}

const orderDiv = (arr, arrToSearch, handleOrder, arrName) => {
  const [open, setOpen] = useState(false)
  return <div className="margin-left-16">
    <button onClick={() => setOpen(!open)} className="margin-top-8 flex align-center align-items-center">
      <i
        className={`accordion-toggle ${open ? "active" : ""}`}
      />
      Editar ordem</button>
    <div className="grid columns-4-tablet gap-8 margin-top-8 margin-bottom-8">
      {open && arr?.map(i => <div className="setting-card small">
        <p style={{ maxWidth: '70%' }}>{findLabel(i, arrToSearch)}</p>
        <div className="flex">
          <button className="arrow-icon arrow-icon-up" onClick={() => handleOrder(i, "up", arrName)} />
          <button className="arrow-icon arrow-icon-down" onClick={() => handleOrder(i, "down", arrName)} />
        </div>
      </div>)}
    </div>
  </div>
}


const Settings = () => {
  const { user } = useAuthContext()
  const [settings, setSettings] = useState(user?.configurations || {})
  const { updateUser, listUsers, createUser } = useAuthActions();
  const { companies, activeProject, projects } = useProjectContext();
  const { setIsLoading } = useDashboardUIActions();
  const { listCompanies, updateCompanyTags } = useProjectActions();
  const [updated, setUpdated] = useState(false)
  const [companyUsers, setCompanyUsers] = useState([])
  const [buildings, setBuildings] = useState([])
  const [newUser, setNewUser] = useState({
    email: "",
    name: "",
    pageViews: [],
    buildingIds: [],
  })
  const [visible, setVisible] = useState({
    users: false,
    tags: false,
    cards: false,
    graphs: false,
    columns: false,
    misc: false,
    files: false,
    buildingInfos: false,
    procurement: false,
    trend: false
  })
  const [newTag, setNewTag] = useState({
    name: '',
    color: '#000000'
  })
  const [files, setFiles] = useState([])

  const [tags, setTags] = useState([])

  const [newFile, setNewFile] = useState({
    file: null,
    name: '',
    building_id: ''
  });

  const currentCompany = companies?.filter(i => i._id === activeProject?.company_id)[0]

  // ⚠️ `currentCompany` acima é `undefined` com frequência, e não só por falta de obra ativa:
  // medido no stg, o `company_id` da obra ativa NÃO está entre as 4 empresas que
  // `GET /project` devolve para a conta. Como as duas seções por-empresa são gatilhadas por
  // `currentCompany?._id`, as duas renderizavam VAZIAS — inclusive a de Ciclos de Compra,
  // que é de produção.
  //
  // A seção de Tendência recebe a LISTA e escolhe dentro dela, em vez de cair na primeira:
  // adivinhar faria o usuário editar a configuração de uma empresa enquanto olha a obra de
  // outra, e salvar ali move o número financeiro de todas as obras da escolhida.
  // `currentCompany` fica intocada — o ProcurementCyclesSection já está em produção com ela.

  // Deep-link: /dashboard/settings?section=procurement (from the Cronograma modal's
  // "Editar ciclos nas Configurações") abre e rola até a seção de ciclos de compra.
  const [searchParams] = useSearchParams()
  const procurementRef = useRef(null)
  useEffect(() => {
    if (searchParams.get("section") === "procurement" && user?.roles?.includes("companyAdmin")) {
      setVisible(v => ({ ...v, procurement: true }))
    }
  }, [searchParams, user])
  useEffect(() => {
    if (visible.procurement && searchParams.get("section") === "procurement" && procurementRef.current) {
      procurementRef.current.scrollIntoView({ behavior: "auto", block: "start" })
    }
  }, [visible.procurement, currentCompany])

  const findUsers = async () => {
    const res = await listUsers(user.companyId[0]._id)
    setCompanyUsers(res?.users || [])

    const buildings = await listCompanies(user.companyId[0]._id);
    setBuildings(buildings.buildings)
  }

  useEffect(() => {
    if (user && currentCompany) {
      setTags(currentCompany?.tags || [])
      setSettings(user.configurations)
      if (user.roles.includes("companyAdmin")) {
        findUsers()
      }
    }
  }, [user, currentCompany])


  const settingsMap = {
    mainCards: { path: ['mainCards'] },
    dashboardGraphCards: { path: ['dashboard', 'cards', 'graph'] },
    dashboardLowerCards: { path: ['dashboard', 'cards', 'lower'] },
    valueCards: { path: ['value', 'cards'] },
    fluxGraph: { path: ['dashboard', 'graphs', 'flux'] },
    financialGraph: { path: ['dashboard', 'graphs', 'financial'] },
    projectionGraph: { path: ['projection', 'graphs', 'projection'] },
    advancementsGraph: { path: ['projection', 'graphs', 'advancements'] },
    balanceGraph: { path: ['projection', 'graphs', 'balance'] },
    offCanvasCards: { path: ['offcanvas', 'cards'] },
    projectionColumns: { path: ['projection', 'columns'] },
    valueColumns: { path: ['value', 'columns'] },
  };

  function updateNestedSetting(obj, path, value, checked) {
    const newObj = { ...obj };
    let ref = newObj;
    for (let i = 0; i < path.length - 1; i++) {
      ref[path[i]] = { ...ref[path[i]] };
      ref = ref[path[i]];
    }
    const arr = ref[path[path.length - 1]] || [];
    ref[path[path.length - 1]] = checked
      ? [...arr, value]
      : arr.filter(i => i !== value);
    return newObj;
  }

  const handleUpdate = (e) => {
    const { name, value, checked } = e.target;
    if (settingsMap[name]) {
      setSettings(prev =>
        updateNestedSetting(prev, settingsMap[name].path, value, checked)
      );
    }
  };

  const handleSend = async () => {
    const newUser = user;
    // Merge over the user's existing configurations so a partial `settings`
    // (e.g. toggled before the load effect populated it) can never wipe
    // untouched top-level keys like mainCards. Each subtree in `settings` is
    // already spread-preserved by updateNestedSetting.
    newUser.configurations = { ...(user?.configurations || {}), ...settings }
    const res = await updateUser(newUser)
    if (res.ok) {
      setUpdated(true)
      setTimeout(() => setUpdated(false), 3000)
    }
  }

  const updateCompanyUser = async (user) => {
    const res = await updateUser(user)
    if (res.code !== 200) {
      alert("Erro ao atualizar usuário, tente novamente")
    }
  }

  const handleCurveChange = (e) => {
    const { name, value } = e.target
    setSettings({
      ...settings,
      curve: {
        ...settings.curve,
        [name]: +value
      }
    })
  }

  const handleHeaderChange = (e) => {
    const { name, checked } = e.target
    setSettings({
      ...settings,
      header: {
        ...settings.header,
        [name]: checked
      }
    })
  }

  const handleOrder = (card, dir, arrName) => {

    let arr;

    switch (arrName) {
      case "mainCards":
        arr = settings.mainCards
        break;
      case "graphCards":
        arr = settings.dashboard.cards.graph
        break;
      case "lowerCards":
        arr = settings.dashboard.cards.lower
        break;
      case "valueCards":
        arr = settings.value.cards
        break;
      case "offCanvasCards":
        arr = settings.offcanvas.cards
        break;
      case "projectionColumns":
        arr = settings.projection.columns
        break;
      default:
        break;
    }

    const currentIndex = arr.indexOf(card)
    const newCards = [...arr]

    if (dir === "up" && currentIndex > 0) {
      [newCards[currentIndex - 1], newCards[currentIndex]] = [newCards[currentIndex], newCards[currentIndex - 1]];
    } else if (dir === "down" && currentIndex < newCards.length - 1) {
      [newCards[currentIndex + 1], newCards[currentIndex]] = [newCards[currentIndex], newCards[currentIndex + 1]];
    }

    switch (arrName) {
      case "mainCards":
        setSettings({ ...settings, mainCards: newCards });
        break;
      case "graphCards":
        setSettings({ ...settings, dashboard: { ...settings.dashboard, cards: { ...settings.dashboard.cards, graph: newCards } } })
        break;
      case "lowerCards":
        setSettings({ ...settings, dashboard: { ...settings.dashboard, cards: { ...settings.dashboard.cards, lower: newCards } } })
        break;
      case "valueCards":
        setSettings({ ...settings, value: { ...settings.value, cards: newCards } })
        break;
      case "offCanvasCards":
        setSettings({ ...settings, offcanvas: { ...settings.offcanvas, cards: newCards } })
        arr = settings.offcanvas.cards
        break;
      case "projectionColumns":
        setSettings({ ...settings, projection: { ...settings.projection, columns: newCards } })
        break;
      default:
        break;
    }
  }

  const handlePermissions = (e) => {
    const { name, value, checked } = e.target
    const users = [...companyUsers]
    if (users[name].permissions.pageViews === undefined) users[name].permissions.pageViews = []
    checked ? users[name].permissions.pageViews.push(value) : users[name].permissions.pageViews = users[name].permissions.pageViews.filter(i => i !== value)
    setCompanyUsers(users)
  }

  const handleUserBuildings = (e) => {
    const { name, value, checked } = e.target
    const users = [...companyUsers]
    checked ? users[name].buildingIds.push(value) : users[name].buildingIds = users[name].buildingIds.filter(i => i !== value)
    setCompanyUsers(users)
  }

  const handleViewsUpdate = (e) => {
    const { value, checked } = e.target
    const userViews = newUser.pageViews;
    if (checked) {
      setNewUser(prev => ({
        ...prev,
        pageViews: [...userViews, value]
      }));
    } else {
      const updatedViews = userViews.filter(i => i !== value);
      setNewUser(prev => ({
        ...prev,
        pageViews: updatedViews
      }));
    }
  }

  const handleNewUser = (e) => {
    const { name, value } = e.target
    setNewUser({
      ...newUser,
      [name]: value
    })
  }

  const submitNewUser = async (e) => {
    e.preventDefault()
    if (!newUser.name || !newUser.email || newUser.buildingIds.length === 0) {
      alert("Preencha todos os campos")
      return
    }
    const newUserResponse = await createUser({
      ...newUser,
      password: 'ampli2024#',
      roles: [],
      companyId: [user.companyId[0]],
      bimProjectsIds: [],
      roles: ["companyUser", "ampli"]
    });

    if (newUserResponse.code === 200) {
      alert(`Usuário criado com sucesso, instruções de acesso foram enviadas para o e-mail ${newUser.email}`)
      setCompanyUsers(prev => ([...prev, { ...newUserResponse.newUser, _id: newUserResponse.userInsertResult.insertedId }]))
      setNewUser({
        email: "",
        name: "",
        pageViews: [],
        buildingIds: [],
      })
    } else {
      alert("Erro ao criar usuário, tente novamente")
    }

  }
  const handleProjectsUpdate = (e) => {
    const { value } = e.target;
    const userProjects = newUser.buildingIds;
    if (!userProjects.includes(value)) {
      setNewUser(prev => ({
        ...prev,
        buildingIds: [...userProjects, value]
      }));
    } else {
      const updatedProjects = userProjects.filter(i => i !== value);
      setNewUser(prev => ({
        ...prev,
        buildingIds: updatedProjects
      }));
    }
  }

  const handleVisible = (section) => {
    setVisible(prev => ({ ...prev, [section]: !prev[section] }))
  }

  const handleNewTag = (e) => {
    const { name, value } = e.target
    setNewTag(prev => ({ ...prev, [name]: value }))
  }

  const handleSendTag = async () => {
    if (newTag.name.trim() === '') return
    const currentTags = [...tags, newTag];
    const res = await updateCompanyTags(currentCompany._id, currentTags)
    if (res.code === 200) {
      setTags(currentTags)
      setNewTag({ name: '', color: '#000000' })
    }
  }

  const handleDeleteTag = async (name) => {
    const currentTags = tags.filter(tag => tag.name !== name);
    const res = await updateCompanyTags(currentCompany._id, currentTags)
    if (res.code === 200) {
      setTags(currentTags)
    }
  }

  const handleFileUpload = async () => {
    if (!newFile.file) return;
    const THRESHOLD = 5 * 1024 * 1024;
    const project = projects.find(i => i.building_id === newFile.building_id)
    const { building_name, company_id } = project;
    let res;
    setIsLoading(true)
    if (newFile.file.size > THRESHOLD) {
      res = await uploadPresigned(newFile, newFile.building_id, building_name, company_id);
    } else {
      res = await uploadSmall(newFile, newFile.building_id, building_name, company_id);
    }
    setIsLoading(false)
    if (res === 200) {
      handleGetFiles();
    } else {
      alert("Erro ao enviar arquivo")
    }
  }

  const handleGetFiles = async () => {
    setIsLoading(true)
    const files = await getFiles('list', projects.map(i => i.building_id));
    setFiles(files.items)
    setIsLoading(false)
  }

  const downloadFile = async (e) => {
    const files = await getFiles('presign', [], e._id);
    const a = document.createElement("a");
    a.href = files.url;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  const handleDeleteFile = async (e) => {
    setIsLoading(true)
    const res = await getFiles('delete', [], e._id);
    handleGetFiles();
    setIsLoading(false)
  }

  useEffect(() => {
    if (visible.files) {
      handleGetFiles()
    }
  }, [visible.files])

  const presentationSubject = isPresentationProject(activeProject) ? activeProject : user
  if (isPresentationProject(presentationSubject)) {
    return <div className="settings"><PresentationModeBadge project={presentationSubject} /></div>
  }

  return <div className="settings">
    <div className="section files">
      <DefaultHeader label={'Arquivos da obra'} visible={visible.files} handle={() => handleVisible('files')} />

      <div className={`${!visible.files ? "display-none" : ""}`}>
        {files.map(i => {
          return <div className="flex" key={i.key}>
            <button style={{ textDecoration: 'underline' }} title="Baixar arquivo" onClick={() => downloadFile(i)}>{i.filename} - {i.building_name}</button>
            {user?.roles.includes("companyAdmin") &&
              <button title="Deletar arquivo" onClick={() => handleDeleteFile(i)}>
                <i class="icon icon-width-24 icon-times background-accent" style={{ backgroundColor: 'tomato' }} aria-hidden="true" />
              </button>
            }
          </div>
        })}
        {user?.roles.includes("companyAdmin") &&
          <div>
            <h3>Enviar arquivo</h3>
            <div className="grid align-center justify-center columns-4-tablet gap-8">
              <input type="text" className="default-input" placeholder="Nome do arquivo" onChange={(e) => setNewFile({ ...newFile, name: e.target.value })} value={newFile.name} />
              <input type="file" onChange={(e) => setNewFile({ ...newFile, file: e.target.files[0] })} />
              <select className="default-input" defaultValue="" value={newFile.building_id} onChange={(e) => setNewFile({ ...newFile, building_id: e.target.value })}>
                <option value="" disabled>Escolha a obra relacionada</option>
                {projects.map(i => {
                  return <option key={i._id} value={i.building_id}>{i.building_name}</option>
                })}
              </select>
              <button className="button button-accent margin-left-16 margin-top-8" onClick={handleFileUpload}>Enviar</button>
            </div>
          </div>}
      </div>

    </div>
    {user?.roles.includes("companyAdmin") && <><div className="section">
      <DefaultHeader label={'Usuários'} visible={visible.users} handle={() => handleVisible('users')} />
      {visible.users && <>
        {companyUsers.length > 0 && companyUsers.map((i, idx) => {
          return <div key={i._id} className="user-card">
            <h3>{i.name} - {i.email}</h3>
            <div className="flex flex-wrap gap-8 margin-left-16 margin-top-8">
              {dashboardPaths.map((path) => {
                const permission = resolveDashboardPathPermission(path)
                return <div key={i.name + permission} className="setting-card">
                  <input type="checkbox" value={permission} checked={i?.permissions?.pageViews.includes(permission)} name={idx} onChange={handlePermissions} />
                  <label>{path.label}</label>
                </div>
              })}
            </div>
            <div className="grid columns-5-tablet gap-8 margin-left-16 margin-top-8 companies">
              {buildings.map(j => {
                return <div key={`${i._id}-${j._id}`}>
                  <input name={idx} onClick={handleUserBuildings} checked={i?.buildingIds?.includes(j._id)} type="checkbox" value={`${j._id}`} />
                  <label>{j?.building_name}</label>
                </div>
              })}
            </div>
            <button className="button button-secondary margin-left-16 margin-top-8 update-user-button" onClick={() => updateCompanyUser(i)}>Atualizar usuário</button>
          </div>
        })}
        <h3>Adicionar Usuário</h3>
        <div className="grid columns-3-tablet gap-8 margin-left-16 margin-top-8">
          <input type="text" className="default-input" placeholder="Nome" value={newUser.name} name="name" onChange={handleNewUser} />
          <input type="text" className="default-input" placeholder="Email" value={newUser.email} name="email" onChange={handleNewUser} />
        </div>
        <div className="grid columns-5-tablet gap-8 margin-left-16 margin-top-8">
          {dashboardPaths.map((path) => {
            const permission = resolveDashboardPathPermission(path)
            return <div key={permission} className="setting-card">
              <input type="checkbox" value={permission} checked={newUser.pageViews.includes(permission)} onChange={handleViewsUpdate} />
              <label>{path.label}</label>
            </div>
          })}
        </div>
        <div className="grid columns-5-tablet gap-8 margin-left-16 margin-top-8 companies">
          {buildings.map(i => {
            return <div key={`${i._id}`}>
              <input onChange={handleProjectsUpdate} name="buildingIds" checked={newUser?.buildingIds?.includes(i._id)} type="checkbox" value={`${i._id}`} />
              <label>{i.building_name}</label>
            </div>
          })}
        </div>
        <button className="button button-accent margin-left-16 margin-top-8" onClick={submitNewUser}>Enviar</button>

      </>}
    </div>


      <div className="section building-infos">
        <DefaultHeader label={'Detalhes de obras'} visible={visible.buildingInfos} handle={() => handleVisible('buildingInfos')} />
        <div className={`${!visible.buildingInfos ? "display-none" : ""}`}>
          <h3 className="bold">Nomes personalizados</h3>
          {projects.map(i => {
            return <CustomNameBuildingRow key={i._id} building={i} />
          })}
        </div>

      </div>

      <div className="section tags">
        <DefaultHeader label={'Lições Aprendidas - Tags'} visible={visible.tags} handle={() => handleVisible('tags')} />
        <div className={`${!visible.tags ? "display-none" : ""}`}>
          <p className="helper">Aqui você pode configurar as tags que estarão disponíveis para serem usadas na seção de Lições Aprendidas. Essas configurações são da construtora.</p>
          <div className="flex flex-wrap gap-16 margin-top-8">
            {tags.length > 0 && tags.map(i => {
              return <div className="flex">
                <p className="example-tag" style={{ backgroundColor: i.color, margin: '0px' }}>{i.name}</p>
                <button title="Deletar tag" onClick={() => handleDeleteTag(i.name)}>
                  <i class="icon icon-width-24 icon-times background-accent" style={{ backgroundColor: 'tomato' }} aria-hidden="true" />
                </button>
              </div>
            })}
          </div>
          <div>
            <h3>Nova tag:</h3>
            <div className="grid columns-4-tablet gap-8">
              <input type="text" name="name" className="default-input" value={newTag.name} placeholder="Nome da tag" onChange={handleNewTag} />
              <input type="color" name="color" className="new-tag-color" value={newTag.color} onChange={handleNewTag} />
              <p className="example-tag" style={{ backgroundColor: newTag.color }}>{newTag.name === "" ? 'Exemplo' : newTag.name}</p>
              <button className="button button-accent margin-left-16 margin-top-8" onClick={handleSendTag}>Adicionar tag</button>

            </div>
          </div>
        </div>
      </div>

      <div className="section" ref={procurementRef}>
        <DefaultHeader label={'Ciclos de Compra do Cronograma de Suprimentos'} visible={visible.procurement} handle={() => handleVisible('procurement')} />
        <div className={`${!visible.procurement ? "display-none" : ""}`}>
          {visible.procurement && currentCompany?._id && <ProcurementCyclesSection company={currentCompany} />}
        </div>
      </div>

      <div className="section">
        <DefaultHeader label={'Tendência e Estoque'} visible={visible.trend} handle={() => handleVisible('trend')} />
        <div className={`${!visible.trend ? "display-none" : ""}`}>
          {visible.trend && (
            companies?.length > 0
              ? <TrendSettingsSection companies={companies} defaultCompany={currentCompany} />
              : <p className="helper">Nenhuma empresa disponível nesta conta para configurar.</p>
          )}
        </div>
      </div>

    </>
    }

    <div className="section">
      <DefaultHeader label={'Cards'} visible={visible.cards} handle={() => handleVisible('cards')} />
      <div className={`${!visible.cards ? "display-none" : ""}`}>
        <div>
          <p className="helper">Aqui você pode configurar quais informações aparecem no aplicativo, mais explicações podem ser encontradas na interrogação (?) de cada item. Essas configurações são somente para essa conta.</p>
          <p className="helper">IMPORTANTE: Nem todos os campos vão apresentar o mesmo título que apresentam aqui, pois algum contém diferentes cálculos para o mesmo campo.</p>
        </div>

        <div className="user-card">
          <div>
            <h3>Principais</h3>
            <p className="helper margin-left-8">Esses cards aparecem na parte superior das telas Dashboard, Valor Agregado e Projeção Financeira.</p>
          </div>
          <div className="grid columns-5-tablet gap-8 margin-left-16 margin-top-8">
            {options.mainCards.map(i => {
              return <div key={i.value} className="setting-card">
                <input type="checkbox" value={i.value} checked={settings?.mainCards?.includes(i.value)} name="mainCards" onChange={handleUpdate} />
                <label>{i.label}</label>
                {i.tooltip && <Tooltip title={i.tooltip} placement="top" message={i.tooltip} />}
              </div>
            })}
          </div>
          {orderDiv(settings?.mainCards, options?.mainCards, handleOrder, "mainCards")}
        </div>

        <div className="user-card">
          <div>
            <h3>Dashboard - Gráfico</h3>
            <p className="helper margin-left-8">Esses cards aparecem na tela de Dashboard, ao lado dos gráficos.</p>
          </div>
          <div className="grid columns-5-tablet gap-8 margin-left-16 margin-top-8">
            {options.dashboard.cards.graph.map(i => {
              return <div key={i.value} className="setting-card">
                <input type="checkbox" value={i.value} checked={settings?.dashboard?.cards?.graph?.includes(i.value)} name="dashboardGraphCards" onChange={handleUpdate} />
                <label>{i.label}</label>
                {i.tooltip && <Tooltip title={i.tooltip} placement="top" message={i.tooltip} />}
              </div>
            })}
          </div>
          {orderDiv(settings?.dashboard?.cards?.graph, options?.dashboard?.cards?.graph, handleOrder, "graphCards")}
        </div>
        <div className="user-card">
          <div>
            <h3>Dashboard - Cards Adicionais</h3>
            <p className="helper margin-left-8">Esses são os cards adicionais que aparecem na parte inferior da tela de Dashboard, após os gráficos.</p>
          </div>
          <div className="grid columns-5-tablet gap-8 margin-left-16 margin-top-8">
            {options.dashboard.cards.lower.map(i => {
              return <div key={i.value} className="setting-card">
                <input type="checkbox" value={i.value} checked={settings?.dashboard?.cards?.lower?.includes(i.value)} name="dashboardLowerCards" onChange={handleUpdate} />
                <label>{i.label}</label>
                {i.tooltip && <Tooltip title={i.tooltip} placement="top" message={i.tooltip} />}
              </div>
            })}
          </div>
          {orderDiv(settings?.dashboard?.cards?.lower, options?.dashboard?.cards?.lower, handleOrder, "lowerCards")}
        </div>

        <div className="user-card">
          <div>
            <h3>Valor Agregado</h3>
            <p className="helper margin-left-8">Esses são os cards adicionais que aparecem na tela de Valor Agregado, abaixo dos cards principais.</p>
          </div>
          <div className="grid columns-5-tablet gap-8 margin-left-16 margin-top-8">
            {options.value.cards.map(i => {
              return <div key={i.value} className="setting-card">
                <input type="checkbox" value={i.value} checked={settings?.value?.cards?.includes(i.value)} name="valueCards" onChange={handleUpdate} />
                <label>{i.label}</label>
                {i.tooltip && <Tooltip title={i.tooltip} placement="top" message={i.tooltip} />}
              </div>
            })}
          </div>
          {orderDiv(settings?.value?.cards, options?.value?.cards, handleOrder, "valueCards")}
        </div>

        <div className="user-card">
          <div>
            <h3>Dados complementares</h3>
            <p className="helper margin-left-8">Esses são os cards que aparecem nas telas de Valor Agregado e Projeção Financeira, quando abertos os detalhes de um item.</p>
          </div>
          <div className="grid columns-5-tablet gap-8 margin-left-16 margin-top-8">
            {options.offcanvas.cards.map(i => {
              return <div key={i.value} className="setting-card">
                <input type="checkbox" value={i.value} checked={settings?.offcanvas?.cards?.includes(i.value)} name="offCanvasCards" onChange={handleUpdate} />
                <label>{i.label}</label>
              </div>
            })}
          </div>
          {orderDiv(settings?.offcanvas?.cards, options?.offcanvas?.cards, handleOrder, "offCanvasCards")}
        </div>
      </div>
    </div>

    <div className="section">
      <DefaultHeader label={'Gráficos'} visible={visible.graphs} handle={() => handleVisible('graphs')} />
      <div className={`${!visible.graphs ? "display-none" : ""}`}>
        <div>
          <p className="helper margin-left-8">Aqui você pode configurar quais barras, linhas e cores aparecem nos gráficos da plataforma.</p>
        </div>
        <div className="user-card">
          <h3>Cores:</h3>
          <div className="grid columns-5-tablet gap-8 margin-left-16 margin-top-8">
            {Object.entries(settings?.graphColors || {}).map(([key, value], index) => (
              <div key={index} className="color-picker">
                <label>{graphColorLabels[key]}</label>
                <input
                  type="color"
                  value={value}
                  onChange={(e) => {
                    setSettings({
                      ...settings,
                      graphColors: { ...settings.graphColors, [key]: e.target.value }
                    });
                  }}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="user-card">
          <h3>Fluxo de Caixa</h3>
          <div className="grid columns-5-tablet gap-8 margin-left-16 margin-top-8">
            {options.dashboard.graphs.flux.map(i => {
              return <div key={i.value} className="setting-card">
                <input type="checkbox" value={i.value} checked={settings?.dashboard?.graphs?.flux?.includes(i.value)} name="fluxGraph" onChange={handleUpdate} />
                <label>{i.label}</label>
              </div>
            })}
          </div>
        </div>

        <div className="user-card">
          <h3>Avanço Físico Financeiro</h3>
          <div className="grid columns-5-tablet gap-8 margin-left-16 margin-top-8">
            {options.projection.graphs.advancements.map(i => {
              return <div key={i.value} className="setting-card">
                <input type="checkbox" value={i.value} checked={settings?.projection?.graphs?.advancements?.includes(i.value)} name="advancementsGraph" onChange={handleUpdate} />
                <label>{i.label}</label>
              </div>
            })}
          </div>
        </div>
      </div>
    </div>

    <div className="section">
      <DefaultHeader label={'Colunas'} visible={visible.columns} handle={() => handleVisible('columns')} />

      <div className={`${!visible.columns ? "display-none" : ""}`}>
        <h3>Projeção Financeira</h3>
        <div className="grid columns-5-tablet gap-8 margin-left-16 margin-top-8">
          {columns.projection.map(i => {
            return <div key={i.value} className="setting-card">
              <input type="checkbox" value={i.value} checked={settings?.projection?.columns?.includes(i.value)} name="projectionColumns" onChange={handleUpdate} />
              <label>{i.label}</label>
            </div>
          })}
        </div>
        {orderDiv(settings?.projection?.columns, columns?.projection, handleOrder, "projectionColumns")}
      </div>
    </div>
    <div className="section">
      {/* <DefaultHeader label={'Geral'} visible={visible.misc} handle={() => handleVisible('misc')} /> */}
      <h2>Geral</h2>
      <div className="misc">
        <div>
          <label>Curva ABC - Limite A</label>
          <input type="number" min={0} max={100} value={settings?.curve?.curveA} name="curveA" onChange={handleCurveChange} />
          <p className="helper margin-left-8">Limite em % para consideração de itens pertencentes ao trecho A da Curva ABC.</p>
        </div>
        <div>
          <label>Curva ABC - Limite B</label>
          <input type="number" min={0} max={100} value={settings?.curve?.curveB} name="curveB" onChange={handleCurveChange} />
          <p className="helper margin-left-8">Limite em % para consideração de itens pertencentes ao trecho B da Curva ABC.</p>
        </div>
        <div>
          <input type="checkbox" checked={settings?.header?.id} name="id" onChange={handleHeaderChange} />
          <label>Mostrar ID da obra no título</label>
        </div>
        <div>
          <input type="checkbox" checked={settings?.header?.lastUpdate} name="lastUpdate" onChange={handleHeaderChange} />
          <label>Mostrar última atualização da obra no título</label>
        </div>
      </div>
    </div>

    <button
      onClick={handleSend}
      disabled={settings === user.configurations}
      className={`button button-${updated ? 'green' : 'secondary'} send-button`}>
      {updated ? 'Salvo!' : 'Salvar'}
    </button>


  </div>
}

export default Settings
